import { areLoopParamsValid, getLoopParams, getLoopSession, setLoopSession } from './loopStorage'

export type LoopCartInitStrategy =
  | { type: 'default' }
  | { type: 'new-loop'; onCartInitialized: () => void }
  | { type: 'returning-loop'; token: string | null; onSyncComplete: () => void }

/**
 * Determines how the Ecomm cart should be initialized based on Loop state.
 *
 * Returns one of three strategies:
 * - `default`: No valid Loop flow detected, standard Ecomm cart init
 * - `new-loop`: skip fetching existing Ecomm cart, create a new one
 * - `returning-loop`: sync Loop cart into existing Ecomm cart
 *
 * Keeping this logic here minimize Loop concerns from bleeding into Cart logic
 */
export function getLoopCartInitStrategy(): LoopCartInitStrategy {
  if (!areLoopParamsValid(getLoopParams())) {
    return { type: 'default' }
  }

  const session = getLoopSession()

  if (session && session.syncRequired) {
    return {
      type: 'returning-loop',
      token: session.token,
      onSyncComplete: () => setLoopSession({ ...session, syncRequired: false }),
    }
  }

  if (session && !session.cartInitialized) {
    return {
      type: 'new-loop',
      onCartInitialized: () => setLoopSession({ ...session, cartInitialized: true }),
    }
  }

  return { type: 'default' }
}

/**
 * Builds a Loop checkout URL using local storage values
 */
export function buildLoopCheckoutUrl(): string {
  const { loop_domain } = getLoopParams()
  const loopSession = getLoopSession()

  if (loop_domain && loopSession?.token) {
    return `https://${loop_domain}/#/cart/v2/${loopSession.token}`
  }
  throw new Error('Failed to build Loop checkout URL')
}
